/**
 * SideNav Component
 *
 * Grouped side navigation with search, status icons and one level of sub-items.
 */
import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Icon } from './icons/Icon';
import { Input } from './inputs/Input';
import type { IconName } from '../icons/names';
import { cn, getTestId } from '../utils';
import { filterSideNav, findMatchRanges, type SideNavItemView } from './side-nav-search';

/** A status shown next to an item: always an icon plus `label` for screen readers and on hover. */
export interface SideNavStatus {
  tone: 'warn' | 'error';
  label: string;
}

/** A sub-item. It has no icon and no children of its own. */
export interface SideNavChildItem {
  /** Unique across the whole nav, groups, children and footer included. */
  id: string;
  label: string;
  href: string;
  /** Extra words the search matches, e.g. synonyms. */
  keywords?: readonly string[];
  status?: SideNavStatus;
}

export interface SideNavItem extends SideNavChildItem {
  icon?: IconName;
  /** Shown when this item or a child is active, or when the search finds a child. */
  children?: readonly SideNavChildItem[];
}

export interface SideNavGroup {
  /** Unique among the groups. */
  id: string;
  /** Heading above the group; the search matches it too. */
  label?: string;
  items: readonly SideNavItem[];
}

/** What `linkComponent` receives; react-router's `Link` fits. */
export interface SideNavLinkProps {
  to: string;
  className?: string;
  'aria-current'?: 'page';
  children?: React.ReactNode;
}

export interface SideNavProps {
  /** Heading of the nav, also its accessible name. */
  title: string;
  /** Heading level of the title; `h2` by default. */
  titleAs?: 'h1' | 'h2' | 'h3';
  /** Keeps the title for screen readers only, where the page already shows it (a selected tab). */
  hideTitle?: boolean;
  groups: readonly SideNavGroup[];
  /** Items below the groups, separated by a line, e.g. rare or destructive settings. */
  footer?: readonly SideNavItem[];
  /** Id of the current page's item; omit when no item is current. */
  activeId?: string;
  /** Called when the nav opens an item itself (Enter in the search); clicks follow the link. */
  onNavigate: (item: SideNavChildItem) => void;
  /** Router link (e.g. react-router `Link`), rendered with `to`; a plain `<a href>` otherwise. */
  linkComponent?: React.ComponentType<SideNavLinkProps>;
  /** Placeholder and accessible label of the search field. */
  searchPlaceholder: string;
  /** Shown when the search finds nothing. */
  emptyText: string;
  className?: string;
  testId?: string;
  id?: string;
}

const NO_ITEMS: readonly SideNavItem[] = [];

const STATUS_STYLE: Record<SideNavStatus['tone'], { icon: IconName; className: string }> = {
  warn: { icon: 'triangle-exclamation', className: 'text-warn-border' },
  error: { icon: 'circle-xmark', className: 'text-error-border' }
};

const rowVariants = cva(
  [
    'relative flex items-center gap-sm rounded-lg px-sm py-xs text-sm text-foreground-base',
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-base',
    // Below md: 52 px rows of a white list, with a chevron.
    'max-md:min-h-13 max-md:rounded-none max-md:px-md max-md:py-0'
  ],
  {
    variants: {
      level: {
        top: 'min-h-9 font-medium',
        child: 'min-h-8 font-normal'
      },
      active: {
        true: [
          'bg-background-input md:shadow-sm',
          'md:before:absolute md:before:inset-y-2.25 md:before:left-0 md:before:w-0.75 md:before:rounded-full md:before:bg-primary-base'
        ],
        false: 'hover:bg-background-muted'
      },
      last: {
        true: '',
        false: 'max-md:border-b max-md:border-border-base'
      }
    },
    defaultVariants: { level: 'top', active: false, last: false }
  }
);

function isTypingTarget(element: Element | null): boolean {
  if (!element) return false;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return true;
  return element.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

function Highlighted({ text, words }: { text: string; words: readonly string[] }) {
  const ranges = findMatchRanges(text, words);
  if (ranges.length === 0) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) parts.push(text.slice(cursor, range.start));
    parts.push(
      <mark key={range.start} className='rounded-sm bg-warn-bg text-inherit'>
        {text.slice(range.start, range.end)}
      </mark>
    );
    cursor = range.end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

interface RowProps {
  item: SideNavItem;
  level: 'top' | 'child';
  isActive: boolean;
  isLast: boolean;
  words: readonly string[];
  linkComponent?: React.ComponentType<SideNavLinkProps>;
}

function SideNavRow({ item, level, isActive, isLast, words, linkComponent }: RowProps) {
  const status = item.status ? STATUS_STYLE[item.status.tone] : undefined;
  const className = rowVariants({ level, active: isActive, last: isLast });
  const ariaCurrent = isActive ? ('page' as const) : undefined;

  const content = (
    <>
      {level === 'top' && item.icon && (
        <Icon
          name={item.icon}
          size='sm'
          className={cn(
            'w-4.5 shrink-0 text-center',
            isActive ? 'text-foreground-base' : 'text-foreground-muted'
          )}
        />
      )}
      <span className='min-w-0 flex-1'>
        <Highlighted text={item.label} words={words} />
      </span>
      {/* The space keeps the label and the status apart in the accessible name. */}
      {item.status && ' '}
      {item.status && status && (
        <span
          title={item.status.label}
          className={cn('inline-flex shrink-0 items-center', status.className)}
        >
          <Icon name={status.icon} size='sm' aria-hidden='true' />
          <span className='sr-only'>{item.status.label}</span>
        </span>
      )}
      {/* Wrapped: FontAwesome's own `display` would override `md:hidden` on the svg. */}
      <span aria-hidden='true' className='inline-flex shrink-0 text-foreground-disabled md:hidden'>
        <Icon name='chevron-down' rotate={270} size='xs' />
      </span>
    </>
  );

  if (linkComponent) {
    const LinkComponent = linkComponent;
    return (
      <LinkComponent to={item.href} aria-current={ariaCurrent} className={className}>
        {content}
      </LinkComponent>
    );
  }
  return (
    <a href={item.href} aria-current={ariaCurrent} className={className}>
      {content}
    </a>
  );
}

interface ItemListProps {
  views: readonly SideNavItemView[];
  words: readonly string[];
  activeId?: string;
  linkComponent?: React.ComponentType<SideNavLinkProps>;
  labelledBy?: string;
}

function SideNavItemList({ views, words, activeId, linkComponent, labelledBy }: ItemListProps) {
  return (
    <ul
      aria-labelledby={labelledBy}
      className='flex flex-col gap-2xs max-md:gap-0 max-md:overflow-hidden max-md:rounded-xl max-md:bg-background-input'
    >
      {views.map((view, index) => {
        const isLastItem = index === views.length - 1;
        return (
          <li key={view.item.id}>
            <SideNavRow
              item={view.item}
              level='top'
              isActive={view.item.id === activeId}
              isLast={isLastItem && view.children.length === 0}
              words={words}
              linkComponent={linkComponent}
            />
            {view.children.length > 0 && (
              <ul className='flex flex-col gap-2xs md:mb-xs md:ml-md md:mt-2xs md:border-l md:border-border-base md:pl-sm max-md:gap-0 max-md:pl-lg'>
                {view.children.map((child, childIndex) => (
                  <li key={child.item.id}>
                    <SideNavRow
                      item={child.item}
                      level='child'
                      isActive={child.item.id === activeId}
                      isLast={isLastItem && childIndex === view.children.length - 1}
                      words={words}
                      linkComponent={linkComponent}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export const SideNav = React.forwardRef<HTMLElement, SideNavProps>(
  (
    {
      title,
      titleAs: TitleTag = 'h2',
      hideTitle = false,
      groups,
      footer = NO_ITEMS,
      activeId,
      onNavigate,
      linkComponent,
      searchPlaceholder,
      emptyText,
      className,
      testId,
      id
    },
    ref
  ) => {
    const [query, setQuery] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const baseId = React.useId();
    const titleId = `${baseId}-title`;
    const resolvedTestId = getTestId({ testId, id });

    const view = React.useMemo(
      () => filterSideNav(groups, footer, query, activeId),
      [groups, footer, query, activeId]
    );

    // "/" moves focus to the search unless the person is typing somewhere else.
    React.useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== '/' || event.defaultPrevented || event.isComposing) return;
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (isTypingTarget(document.activeElement)) return;
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        // A hidden nav (mobile detail view) cannot take focus; leave the key alone then.
        if (document.activeElement === input) event.preventDefault();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Enter that confirms an IME composition is not a search submit.
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        if (view.firstHit) {
          onNavigate(view.firstHit);
          setQuery('');
        }
      } else if (event.key === 'Escape' && query !== '') {
        event.preventDefault();
        setQuery('');
      }
    };

    return (
      <nav
        ref={ref}
        id={id}
        aria-labelledby={titleId}
        data-testid={resolvedTestId}
        className={cn(
          'flex flex-col gap-md self-start md:pb-md md:pl-2xs md:pr-sm md:pt-2xs',
          className
        )}
      >
        <TitleTag
          id={titleId}
          className={hideTitle ? 'sr-only' : 'text-lg font-semibold text-foreground-base'}
        >
          {title}
        </TitleTag>

        {/* A wrapping label, so the input needs no `id` (Input would turn it into a test id). */}
        <label className='block'>
          <span className='sr-only'>{searchPlaceholder}</span>
          <Input
            ref={inputRef}
            type='search'
            height='sm'
            autoComplete='off'
            placeholder={searchPlaceholder}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            testId={resolvedTestId ? `${resolvedTestId}-search` : undefined}
            className='[&_input::-webkit-search-cancel-button]:appearance-none'
            leftContent={<Icon name='magnifying-glass' size='sm' />}
            rightContent={
              <kbd
                aria-hidden='true'
                className='rounded-sm border max-md:hidden border-border-base bg-background-base px-xs font-sans text-xs text-foreground-muted'
              >
                /
              </kbd>
            }
          />
        </label>

        {view.isEmpty ? (
          <p role='status' aria-live='polite' className='px-sm py-md text-sm text-foreground-muted'>
            {emptyText}
          </p>
        ) : (
          <div className='flex flex-col gap-md'>
            {view.groups.map((groupView, index) => {
              const labelId = groupView.group.label ? `${baseId}-group-${index}` : undefined;
              return (
                <div key={groupView.group.id} className='flex flex-col gap-2xs'>
                  {groupView.group.label && (
                    <span
                      id={labelId}
                      className='px-sm pb-xs text-xs font-semibold text-foreground-muted'
                    >
                      {groupView.group.label}
                    </span>
                  )}
                  <SideNavItemList
                    views={groupView.items}
                    words={view.words}
                    activeId={activeId}
                    linkComponent={linkComponent}
                    labelledBy={labelId}
                  />
                </div>
              );
            })}
            {view.footer.length > 0 && (
              <div className='md:border-t md:border-border-base md:pt-sm'>
                <SideNavItemList
                  views={view.footer}
                  words={view.words}
                  activeId={activeId}
                  linkComponent={linkComponent}
                />
              </div>
            )}
          </div>
        )}
      </nav>
    );
  }
);

SideNav.displayName = 'SideNav';
